/**
 * Descripciones cortas por página para el command palette (⌘K).
 *
 * Clave: el `id` de la entrada, que es `routerLink.join('/')` (ej: '/sales/invoices').
 * Valor: frase breve que explica qué hace la página. Se muestra como subtítulo
 * (`Sección · descripción`) y también alimenta la búsqueda por texto.
 *
 * Mantener aquí en vez de en `app.menu.ts` para no inflar el modelo de navegación;
 * son metadatos específicos del buscador.
 */
export const ENTRY_DESCRIPTIONS: Record<string, string> = {
  // Panel principal
  '/dashboard': 'Panel principal',

  // Accesos rápidos
  '/sales/invoices/quick-access': 'Crea una factura de venta rápidamente',
  '/sales/orders/quick-access': 'Crea una nueva cotización de venta',
  '/sales/delivery-notes/quick-access': 'Genera una nota de entrega',
  '/sales/return-notes/quick-access': 'Registra una devolución de mercancía',
  '/sales/delivery-quotations/quick-access': 'Crea una cotización de entrega',
  '/inventory/movements/quick-access-balances': 'Consulta existencias por almacén',
  '/crm/customers/quick-access': 'Accede al listado de clientes',

  // Ventas
  '/sales/invoices': 'Emite y gestiona facturas de venta',
  '/sales/orders': 'Cotizaciones y pedidos de venta',
  '/sales/quotations-templates': 'Plantillas reutilizables de cotización',
  '/sales/delivery-notes': 'Despacho de mercancía a clientes',
  '/sales/dispatch-guides': 'Guías de despacho, transporte, entrega y evidencia logística',
  '/sales/return-notes': 'Devoluciones de clientes',
  '/sales/delivery-quotations': 'Cotizaciones de despacho y logística',
  '/sales/freights': 'Configura cargos de flete y transporte',
  '/sales/discounts': 'Reglas y promociones de descuento',
  '/sales/reports/reports-dashboard': 'Resumen de ventas del día',
  '/sales/reports/sales-by-period': 'Comparativa de ventas por fecha',
  '/sales/reports/sales-by-seller': 'Desempeño de ventas por vendedor',
  '/sales/reports/sales-by-product': 'Productos más vendidos',
  '/sales/reports/geographic-analysis': 'Ventas por zona geográfica',
  '/sales/reports/profitability': 'Margen y utilidad por categoría',
  '/sales/reports/trends-analysis': 'Tendencias y comparativas de ventas',
  '/sales/reports/customer-frequency': 'Recurrencia de compra de clientes',
  '/sales/reports/top-customers': 'Clientes con mayor facturación',
  '/sales/reports/detailed-sales': 'Detalle línea por línea de ventas',

  // Compras
  '/purchases/material-requests': 'Solicitudes internas de materiales',
  '/purchases/procurement/rfq-list': 'RFQ enviadas a proveedores',
  '/purchases/procurement/quotations-list': 'Cotizaciones recibidas de proveedores',
  '/purchases/purchase-orders': 'Órdenes de compra a proveedores',
  '/purchases/purchase-receipts': 'Recepción de mercancía comprada',
  '/purchases/purchase-matching': 'Concilia órdenes, recepciones y facturas',
  '/purchases/supplier-returns': 'Devoluciones a proveedores',
  '/purchases/suppliers': 'Directorio de proveedores',
  '/purchases/suppliers/groups': 'Agrupa proveedores por categoría',
  '/purchases/purchase-reports': 'Análisis y reportes de compras',
  '/purchases/item-prices': 'Historial de precios de compra',

  // Inventario
  '/inventory/products': 'Catálogo de productos',
  '/inventory/prices': 'Listas y precios de productos',
  '/inventory/movements/balances': 'Existencias actuales por producto',
  '/inventory/movements/list': 'Entradas y salidas de inventario',
  '/inventory/movements/ledger': 'Kardex de movimientos de stock',
  '/inventory/warehouses': 'Almacenes y ubicaciones',
  '/inventory/catalogo': 'Catálogo visual de productos',
  '/inventory/valuation/summary': 'Resumen de valorización de inventario',
  '/inventory/valuation/by-category': 'Valorización por categoría',
  '/inventory/valuation/by-product': 'Valorización por producto',
  '/inventory/valuation/by-warehouse': 'Valorización por almacén',
  '/inventory/categories': 'Categorías de productos',
  '/inventory/units-of-measure': 'Unidades de medida',
  '/inventory/attributes': 'Atributos y variantes de producto',
  '/inventory/alert-config': 'Alertas de stock mínimo',
  '/inventory/diagnostic': 'Diagnóstico de inconsistencias de inventario',

  // CRM
  '/crm/customers': 'Gestión de clientes',
  '/crm/credit-lines': 'Líneas de crédito de clientes',
  '/crm/suppliers': 'Gestión de proveedores',
  '/crm/supplier-groups': 'Agrupación de proveedores',
  '/crm/collaborators': 'Colaboradores y contactos',
  '/crm/sellers': 'Gestión de vendedores',
  '/crm/sellers/users-sellers-list': 'Vínculo de usuarios con vendedores',
  '/crm/commissions': 'Comisiones de vendedores',
  '/crm/commissions/category': 'Configura comisiones por categoría',
  '/crm/commissions/config': 'Reglas de comisiones automáticas',

  // Finanzas
  '/finance/accounts': 'Cuentas bancarias y de caja',
  '/finance/arp': 'Saldos por cobrar y por pagar',
  '/finance/arp/advances': 'Anticipos recibidos de clientes',
  '/finance/transactions': 'Movimientos financieros',
  '/finance/expenses/dashboard': 'Panel de análisis de gastos',
  '/finance/expenses/list': 'Registro de gastos',
  '/finance/expenses/fixed': 'Plantillas de gastos recurrentes',
  '/finance/expenses/categories': 'Categorías para clasificar gastos',
  '/finance/reports/aging': 'Antigüedad de saldos por cobrar y pagar',
  '/finance/reports/account-statement': 'Estado de cuenta por entidad',
  '/finance/reports/bank-book': 'Libro de movimientos bancarios',
  '/finance/reports/cash-flow': 'Proyección y flujo de caja',
  '/finance/reports/income-expense': 'Comparativa de ingresos y egresos',
  '/finance/reports/financial-health': 'Tablero operativo: caja, cartera, inventario y resultado del periodo',

  // E-Commerce
  '/ecommerce/reports/dashboard': 'Panel de la tienda en línea',
  '/ecommerce/reports/sales': 'Ventas del e-commerce',
  '/ecommerce/reports/tops': 'Rankings de productos y clientes',
  '/ecommerce/customers': 'Cuentas de clientes de la tienda',
  '/ecommerce/categories': 'Categorías publicadas en la tienda',
  '/ecommerce/products': 'Productos publicados en la tienda',
  '/ecommerce/config': 'Ajustes de la tienda en línea',

  // Recursos Humanos
  '/rrhh/employees': 'Ficha y gestión de empleados',
  '/rrhh/contracts': 'Contratos laborales',
  '/rrhh/contracts/annexes': 'Anexos y modificaciones de contrato',
  '/rrhh/family-members': 'Carga familiar de empleados',
  '/rrhh/vacations': 'Solicitudes y períodos de vacaciones',
  '/rrhh/absences': 'Ausencias e inasistencias',
  '/rrhh/suspensions': 'Suspensiones laborales',
  '/rrhh/departments': 'Estructura de departamentos',
  '/rrhh/positions': 'Cargos y puestos de trabajo',
  '/rrhh/cost-centers': 'Centros de costo',
  '/rrhh/employee-types': 'Clasificación de empleados',
  '/rrhh/contract-types': 'Tipos de contrato',
  '/rrhh/contract-templates': 'Plantillas de contrato',
  '/rrhh/contract-clauses': 'Cláusulas reutilizables de contrato',
  '/rrhh/shifts': 'Turnos de trabajo',
  '/rrhh/work-schedules': 'Horarios laborales',
  '/rrhh/work-crews': 'Cuadrillas de trabajo',
  '/rrhh/holidays': 'Calendario de feriados',
  '/rrhh/vacation-rules': 'Reglas de cálculo de vacaciones',
  '/rrhh/dashboard': 'Indicadores de RRHH',
  '/rrhh/reports': 'Reporte de pasivo vacacional',

  // Nómina
  '/payroll/dashboard': 'Panel general de nómina',
  '/payroll/periods': 'Períodos de nómina',
  '/payroll/incidents': 'Novedades del período de nómina',
  '/payroll/loans': 'Préstamos a empleados',
  '/payroll/health': 'Prevalidación de salud de la nómina',
  '/payroll/transactions': 'Recibos de pago de empleados',
  '/payroll/settlements': 'Liquidaciones de prestaciones (LOTTT)',
  '/payroll/islr-estimations': 'Estimaciones de ISLR (ARI)',
  '/payroll/concepts': 'Catálogo de conceptos de nómina',
  '/payroll/employee-concepts': 'Asignación de conceptos por empleado',
  '/payroll/legal-catalogs': 'Catálogos legales ISLR, IVSS y UT',
  '/payroll/reports': 'Reportes legales de nómina',
  '/payroll/audit': 'Auditoría de nómina',

  // Configuración general
  '/general-settings/settings': 'Configuración general de la empresa',
  '/general-settings/company/fiscal-data': 'Datos fiscales de la empresa',
  '/general-settings/company/customization': 'Personaliza la apariencia de la app',
  '/general-settings/document-sequences': 'Numeración de documentos',
  '/general-settings/prices-lists': 'Listas de precios comerciales',
  '/general-settings/taxes': 'Configuración de impuestos',
  '/general-settings/payment-terms': 'Condiciones de pago',
  '/general-settings/payment-methods': 'Formas de pago aceptadas',
  '/general-settings/fiscal-config': 'Parámetros fiscales',
  '/general-settings/tax-categories': 'Categorías de impuestos',
  '/general-settings/fiscal-unit-values': 'Valores de la unidad tributaria',
  '/general-settings/tax-rates': 'Alícuotas y tasas de impuestos',
  '/general-settings/islr-concepts': 'Conceptos de retención ISLR',
  '/general-settings/currencies-exchanges/currencies': 'Monedas configuradas',
  '/general-settings/currencies-exchanges/exchange-rates': 'Tasas de cambio',
  '/general-settings/currencies-exchanges/series': 'Series de tasa interna para métodos de pago',
  '/general-settings/zones/zones': 'Zonas geográficas',
  '/general-settings/zones/sub-zones': 'Sub-zonas geográficas',
  '/general-settings/users/users': 'Gestión de usuarios',
  '/general-settings/users/roles': 'Roles y permisos de usuario',
  '/general-settings/audit-logs': 'Bitácora de auditoría del sistema',

  // Tributario
  '/tributario/retenciones/comprobantes': 'Comprobantes de retención emitidos',
  '/tributario/retenciones/sufridas': 'Retenciones aplicadas por terceros',
  '/tributario/libros-fiscales/ventas': 'Libro fiscal de ventas',
  '/tributario/libros-fiscales/compras': 'Libro fiscal de compras',
  '/tributario/libros-fiscales/retenciones-iva': 'Libro de retenciones de IVA',
  '/tributario/libros-fiscales/retenciones-islr': 'Libro de retenciones de ISLR',
  '/tributario/libros-fiscales/igtf': 'Declaración de IGTF',
  '/tributario/igtf/registros': 'Registros de IGTF',
  '/tributario/reportes/resumen': 'Reportes fiscales y tributarios',
  '/tributario/seguridad-fiscal': 'Panel de seguridad fiscal',

  // Punto de Venta
  '/pos': 'Inicia una sesión de punto de venta',
  '/pos/settings/profiles': 'Perfiles de configuración del POS',
  '/pos/settings/registers': 'Cajas registradoras',
  '/pos/settings/cashiers': 'Gestión de cajeros',
  '/pos/settings/permissions': 'Permisos de los cajeros',
  '/pos/settings/sessions': 'Historial de turnos de caja',

  // Soporte
  '/soporte/tickets': 'Tus tickets de soporte',
  '/soporte/roadmap': 'Hoja de ruta del producto',
  '/soporte-admin/dashboard': 'Panel de gestión de soporte',
  '/soporte-admin/tickets': 'Gestión de tickets de soporte',
  '/soporte-admin/config': 'Configuración de SLA de soporte',
  '/soporte-admin/plantillas': 'Plantillas de respuesta a tickets',

  // Gestión empresarial (admin)
  '/admin': 'Administración de tenants',
  '/admin/plans': 'Planes de suscripción',
  '/admin/modules': 'Módulos del sistema',
  '/admin/permissions': 'Catálogo de permisos',
  '/admin/contact-messages': 'Mensajes del formulario de contacto',
};
